import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  DeleteOutline as DeleteIcon,
  Forum as ForumIcon,
  MenuBook as KnowledgeIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { ChatbotBuilder } from '../components/ChatbotBuilder';
import { PlansNotice } from '../components/function-builder/shared';
import { DeleteConfirmationModal } from '../components/common';
import { RelativeTime } from '../components/RelativeTime';

interface Chatbot {
  id: string;
  name: string;
  display_name?: string;
  description?: string;
  metadata?: {
    is_chatbot?: boolean;
    chatbot_name?: string;
    knowledge_categories?: string[];
    branding?: { name?: string; primaryColor?: string; logoUrl?: string };
  };
  created_at: string;
}

interface Category {
  id: string;
  name: string;
}

const STEPS = [
  { title: 'Describe it', body: 'Give it a name and say what it should help with, in plain words.' },
  { title: 'Add your documents', body: 'Optional. Upload help articles, policies or manuals and it answers from them.' },
  { title: 'Try it and put it on your site', body: 'Chat with it, match your brand colors, then copy the embed code.' },
];

const chatbotName = (c: Chatbot) => c.metadata?.branding?.name || c.metadata?.chatbot_name || c.display_name || c.name;

export const ChatbotGenerationView: React.FC = () => {
  const { user, hasActiveSubscription } = useAuth();
  const navigate = useNavigate();
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderPrefill, setBuilderPrefill] = useState<{ name?: string; instructions?: string }>({});
  const [pendingPrefill, setPendingPrefill] = useState<{ name?: string; instructions?: string } | null>(null);
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [toDelete, setToDelete] = useState<Chatbot | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const promptParam = searchParams.get('prompt');
  const nameParam = searchParams.get('name');

  const loadChatbots = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(null);
    try {
      const idToken = await user.getIdToken();
      const response = await fetch('/api/v2/ai-functions/?latest_only=true&include_inactive=false', {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!response.ok) throw new Error(`Couldn't load your chatbots (${response.status}).`);
      const data = await response.json();
      const items: Chatbot[] = (data.items || []).filter((f: Chatbot) => f.metadata?.is_chatbot === true);
      items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setChatbots(items);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Couldn't load your chatbots.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadCategories = useCallback(async () => {
    if (!user) return;
    try {
      const idToken = await user.getIdToken();
      const currentResp = await fetch('/api/v2/customers/current', {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!currentResp.ok) return;
      const customerId = (await currentResp.json())?.data?.customer_id;
      if (!customerId) return;
      const catsResp = await fetch(`/api/v2/customers/${customerId}/categories`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (catsResp.ok) setCategories((await catsResp.json()).data?.items || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, [user]);

  useEffect(() => {
    loadChatbots();
    loadCategories();
  }, [loadChatbots, loadCategories]);

  useEffect(() => {
    if (promptParam === null && nameParam === null) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('prompt');
      next.delete('name');
      return next;
    }, { replace: true });
    setPendingPrefill({
      name: nameParam?.slice(0, 100) || undefined,
      instructions: promptParam?.slice(0, 4000) || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptParam, nameParam]);

  useEffect(() => {
    if (!pendingPrefill || !hasActiveSubscription) return;
    setBuilderPrefill(pendingPrefill);
    setPendingPrefill(null);
    setShowBuilder(true);
  }, [pendingPrefill, hasActiveSubscription]);

  const startNew = () => {
    if (!hasActiveSubscription) {
      navigate('/dashboard/subscription');
      return;
    }
    setBuilderPrefill({});
    setShowBuilder(true);
  };

  const closeBuilder = () => {
    setShowBuilder(false);
    setBuilderPrefill({});
  };

  const deleteChatbot = async () => {
    if (!toDelete || !user) return;
    const idToken = await user.getIdToken();
    const response = await fetch(`/api/v2/ai-functions/${toDelete.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!response.ok) throw new Error("Couldn't delete the chatbot. Please try again.");
    setChatbots((prev) => prev.filter((c) => c.id !== toDelete.id));
  };

  if (showBuilder) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={closeBuilder} sx={{ mb: 1, textTransform: 'none' }}>
          Chatbots
        </Button>
        <ChatbotBuilder
          initialName={builderPrefill.name}
          initialInstructions={builderPrefill.instructions}
          onComplete={(functionId) => navigate(`/chatbot/studio/${functionId}?new=1`)}
          onCancel={closeBuilder}
        />
      </Box>
    );
  }

  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Chatbots
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Chat assistants that remember the conversation, answer from your documents and sit on your website.
          </Typography>
        </Box>
        {chatbots.length > 0 && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={startNew} disabled={!hasActiveSubscription}>
            New chatbot
          </Button>
        )}
      </Box>

      {!hasActiveSubscription && (
        <Box sx={{ mb: 3 }}>
          <PlansNotice>Creating and testing chatbots needs a plan.</PlansNotice>
        </Box>
      )}

      {loadError && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={loadChatbots}>Retry</Button>}>
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : chatbots.length === 0 && !loadError ? (
        <Paper
          variant="outlined"
          sx={(theme) => ({
            p: { xs: 2.5, md: 4 },
            borderRadius: 2,
            borderColor: alpha(theme.palette.primary.main, 0.35),
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          })}
        >
          <Box sx={{ maxWidth: 820, mx: 'auto' }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Create your first chatbot
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              It takes a couple of minutes and no technical knowledge.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {STEPS.map((step, i) => (
                <Grid item xs={12} md={4} key={step.title}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: 14, bgcolor: 'primary.main' }}>{i + 1}</Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.body}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={startNew} disabled={!hasActiveSubscription}>
                New chatbot
              </Button>
              <Link
                component={RouterLink}
                to="/dashboard/guide"
                variant="body2"
                underline="hover"
                color="text.secondary"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                <ForumIcon sx={{ fontSize: 16 }} />
                Not sure a chatbot is right? Ask the Build Guide
              </Link>
            </Box>
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {chatbots.map((chatbot) => {
            const name = chatbotName(chatbot);
            const color = chatbot.metadata?.branding?.primaryColor;
            const logo = chatbot.metadata?.branding?.logoUrl;
            const knowledge = (chatbot.metadata?.knowledge_categories || [])
              .map(categoryName)
              .filter((n): n is string => !!n);
            return (
              <Grid item xs={12} md={6} lg={4} key={chatbot.id}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Avatar src={logo || undefined} sx={{ bgcolor: color || 'primary.main', width: 40, height: 40 }}>
                        {name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
                          {name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Created <RelativeTime value={chatbot.created_at} variant="caption" />
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {chatbot.description || 'No description'}
                    </Typography>
                    {knowledge.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <KnowledgeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {knowledge.map((n) => (
                          <Chip key={n} label={n} size="small" variant="outlined" />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No documents yet. Answers from its instructions only.
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate(`/chatbot/studio/${chatbot.id}`)}
                      disabled={!hasActiveSubscription}
                    >
                      Open
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      Test, customize, add to website
                    </Typography>
                    <Tooltip title="Delete chatbot">
                      <IconButton size="small" onClick={() => setToDelete(chatbot)} aria-label={`Delete ${name}`}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {toDelete && (
        <DeleteConfirmationModal
          open
          onClose={() => setToDelete(null)}
          onConfirm={deleteChatbot}
          title="Delete chatbot"
          itemName={chatbotName(toDelete)}
          itemType="chatbot"
          isDeactivation={false}
          message={`Delete "${chatbotName(toDelete)}"? This can't be undone, and any website using its embed code will stop getting answers. Your uploaded documents stay in the Knowledge Hub.`}
        />
      )}
    </Box>
  );
};

export default ChatbotGenerationView;
